import Phaser from 'phaser';

import { SCREEN_SIZES } from '@/shared/config/ui';

export type AnswerBoxPosition = 'left' | 'middle' | 'right';

const MOBILE_LABEL_MAX_FONT_PX = 34;
const MOBILE_LABEL_PREFERRED_MIN_FONT_PX = 8;
const DESKTOP_LABEL_MAX_FONT_PX = 44;
const DESKTOP_LABEL_PREFERRED_MIN_FONT_PX = 10;
const LABEL_ABSOLUTE_MIN_FONT_PX = 5;
const LABEL_FONT_SIZE_STEP_PX = 1;
const LABEL_MAX_CHAR_COUNT_FOR_LARGE_FONT = 5;
const LABEL_MIN_CHAR_COUNT_FOR_LARGE_FONT = 24;
const LABEL_FONT_FAMILY = 'ModernEra, Helvetica, Arial, sans-serif';
const LABEL_FONT_STYLE = '900';

function getLabelInsets(
	position: AnswerBoxPosition,
	boxWidth: number,
	boxHeight: number,
): { top: number; right: number; bottom: number; left: number } {
	if (position === 'left') {
		return {
			top: boxHeight * 0.24,
			right: boxWidth * 0.2,
			bottom: boxHeight * 0.28,
			left: boxWidth * 0.14,
		};
	}

	if (position === 'right') {
		return {
			top: boxHeight * 0.24,
			right: boxWidth * 0.14,
			bottom: boxHeight * 0.24,
			left: boxWidth * 0.2,
		};
	}

	return {
		top: boxHeight * 0.26,
		right: boxWidth * 0.16,
		bottom: boxHeight * 0.26,
		left: boxWidth * 0.16,
	};
}

function getPreferredLabelFontSizePx(labelText: string, viewportWidth: number): number {
	const characterCount = labelText.replace(/\s+/g, '').length;
	const maxFontSizePx = viewportWidth >= SCREEN_SIZES.DESKTOP ? DESKTOP_LABEL_MAX_FONT_PX : MOBILE_LABEL_MAX_FONT_PX;
	const preferredMinFontSizePx =
		viewportWidth >= SCREEN_SIZES.DESKTOP
			? DESKTOP_LABEL_PREFERRED_MIN_FONT_PX
			: MOBILE_LABEL_PREFERRED_MIN_FONT_PX;
	const normalizedCharacterCount = Phaser.Math.Clamp(
		(characterCount - LABEL_MAX_CHAR_COUNT_FOR_LARGE_FONT) /
			(LABEL_MIN_CHAR_COUNT_FOR_LARGE_FONT - LABEL_MAX_CHAR_COUNT_FOR_LARGE_FONT),
		0,
		1,
	);

	return Math.round(Phaser.Math.Linear(maxFontSizePx, preferredMinFontSizePx, normalizedCharacterCount));
}

function getScaledLabelFontSizePx(fontSizePx: number, position: AnswerBoxPosition, renderScaleX: number): number {
	const fallbackShrink = position === 'middle' ? 1 : 0.98;

	return fontSizePx * renderScaleX * fallbackShrink;
}

function getWrappedLabelLines(label: Phaser.GameObjects.Text, rawLabel: string, maxWidth: number): string[] {
	const trimmedLabel = rawLabel.trim();
	if (!trimmedLabel) {
		return [];
	}

	label.style.syncFont(label.canvas, label.context);

	const paragraphs = trimmedLabel.split(/\r?\n/);
	const wrappedLines: string[] = [];

	paragraphs.forEach((paragraph) => {
		const words = paragraph.trim().split(/\s+/).filter(Boolean);
		if (!words.length) {
			wrappedLines.push('');
			return;
		}

		let currentLine = '';
		words.forEach((word) => {
			const nextLine = currentLine ? `${currentLine} ${word}` : word;
			if (label.context.measureText(nextLine).width <= maxWidth) {
				currentLine = nextLine;
				return;
			}

			if (currentLine) {
				wrappedLines.push(currentLine);
				currentLine = '';
			}

			let wordPart = '';
			Array.from(word).forEach((character) => {
				const nextWordPart = `${wordPart}${character}`;
				if (wordPart && label.context.measureText(nextWordPart).width > maxWidth) {
					wrappedLines.push(wordPart);
					wordPart = character;
					return;
				}

				wordPart = nextWordPart;
			});

			currentLine = wordPart;
		});

		if (currentLine) {
			wrappedLines.push(currentLine);
		}
	});

	return wrappedLines;
}

export function syncAnswerLabelLayout({
	label,
	position,
	boxWidth,
	boxHeight,
}: {
	label: Phaser.GameObjects.Text;
	position: AnswerBoxPosition;
	boxWidth: number;
	boxHeight: number;
}) {
	const insets = getLabelInsets(position, boxWidth, boxHeight);
	const contentWidth = Math.max(1, boxWidth - insets.left - insets.right);
	const contentHeight = Math.max(1, boxHeight - insets.top - insets.bottom);
	const centerX = -boxWidth / 2 + insets.left + contentWidth / 2;
	const centerY = -boxHeight / 2 + insets.top + contentHeight / 2 + boxHeight / 2;

	label.setAlign('center');
	label.setPosition(centerX, centerY);
}

export function syncAnswerLabelText({
	label,
	position,
	boxWidth,
	boxHeight,
	rawLabel,
	viewportWidth,
	renderScaleX,
}: {
	label: Phaser.GameObjects.Text;
	position: AnswerBoxPosition;
	boxWidth: number;
	boxHeight: number;
	rawLabel: string;
	viewportWidth: number;
	renderScaleX: number;
}) {
	const trimmedLabel = rawLabel.trim();
	const insets = getLabelInsets(position, boxWidth, boxHeight);
	const contentWidth = Math.max(1, boxWidth - insets.left - insets.right);
	const contentHeight = Math.max(1, boxHeight - insets.top - insets.bottom);
	const maxFontSizePx = getPreferredLabelFontSizePx(trimmedLabel, viewportWidth);
	let currentFontSizePx = maxFontSizePx;

	label.setAlign('center');

	if (!trimmedLabel) {
		label.setText('');
		syncAnswerLabelLayout({ label, position, boxWidth, boxHeight });
		return;
	}

	while (currentFontSizePx >= LABEL_ABSOLUTE_MIN_FONT_PX) {
		label.setFontFamily(LABEL_FONT_FAMILY);
		label.setFontStyle(LABEL_FONT_STYLE);
		label.setFontSize(getScaledLabelFontSizePx(currentFontSizePx, position, renderScaleX));
		label.setText(getWrappedLabelLines(label, trimmedLabel, contentWidth).join('\n'));

		if (label.width <= contentWidth && label.height <= contentHeight) {
			break;
		}

		currentFontSizePx -= LABEL_FONT_SIZE_STEP_PX;
	}

	if (currentFontSizePx < LABEL_ABSOLUTE_MIN_FONT_PX) {
		label.setFontFamily(LABEL_FONT_FAMILY);
		label.setFontStyle(LABEL_FONT_STYLE);
		label.setFontSize(getScaledLabelFontSizePx(LABEL_ABSOLUTE_MIN_FONT_PX, position, renderScaleX));
		label.setText(getWrappedLabelLines(label, trimmedLabel, contentWidth).join('\n'));
	}

	syncAnswerLabelLayout({ label, position, boxWidth, boxHeight });
}
